/**
 * Administrative regions and postal-code rules for the GCC countries, keyed by
 * the ISO code {@link getCountryForCurrency} returns.
 *
 * WHY THIS EXISTS: the address forms offered Indian states to every tenant, so
 * an Omani supplier form listed Andhra Pradesh, Assam and Bihar. There is no
 * `country` column by design (see the note in `tax-regime.ts`) — the tenant's
 * immutable `default_currency` is the statutory country — so the region list is
 * derived from currency the same way the dial code and tax regime already are.
 *
 * INDIA IS DELIBERATELY ABSENT. Its state list already exists twice in
 * `apps/admin`, in `constants/indian-states.ts` (locations) and
 * `features/suppliers/constants.ts` (suppliers), and the two disagree on both
 * spelling ("Jammu & Kashmir" vs "Jammu and Kashmir") and ordering. Those
 * strings are stored on rows and, for locations, feed the CGST/SGST-vs-IGST
 * comparison in GSTR-1, the Tally export and four PDF renderers. Unifying them
 * here would silently change what an Indian tenant can pick and what their
 * existing rows compare equal to, so both lists stay exactly where they are.
 *
 * The data below is compiled from public administrative divisions, and was
 * reviewed and signed off by the business on 2026-09-09.
 */

/** Administrative regions offered in the address "State" picker, per country. */
export const COUNTRY_REGIONS: Readonly<Record<string, readonly string[]>> = Object.freeze({
    AE: Object.freeze([
        'Abu Dhabi',
        'Dubai',
        'Sharjah',
        'Ajman',
        'Umm Al Quwain',
        'Ras Al Khaimah',
        'Fujairah',
    ]),
    SA: Object.freeze([
        'Riyadh',
        'Makkah',
        'Madinah',
        'Eastern Province',
        'Asir',
        'Tabuk',
        'Hail',
        'Northern Borders',
        'Jazan',
        'Najran',
        'Al Bahah',
        'Al Jawf',
        'Qassim',
    ]),
    QA: Object.freeze([
        'Doha',
        'Al Rayyan',
        'Al Wakrah',
        'Al Khor',
        'Al Shamal',
        'Al Daayen',
        'Umm Salal',
        'Al Shahaniya',
    ]),
    OM: Object.freeze([
        'Muscat',
        'Dhofar',
        'Musandam',
        'Al Buraimi',
        'Ad Dakhiliyah',
        'Al Batinah North',
        'Al Batinah South',
        'Ash Sharqiyah North',
        'Ash Sharqiyah South',
        'Ad Dhahirah',
        'Al Wusta',
    ]),
    BH: Object.freeze(['Capital', 'Muharraq', 'Northern', 'Southern']),
    KW: Object.freeze([
        'Al Asimah',
        'Hawalli',
        'Farwaniya',
        'Mubarak Al-Kabeer',
        'Ahmadi',
        'Jahra',
    ]),
});

/**
 * What a country calls the region in an address, for the field label. Using
 * "State" for a UAE address is simply the wrong word.
 */
export const REGION_LABEL: Readonly<Record<string, string>> = Object.freeze({
    IN: 'State',
    AE: 'Emirate',
    SA: 'Region',
    QA: 'Municipality',
    OM: 'Governorate',
    BH: 'Governorate',
    KW: 'Governorate',
});

/**
 * Postal-code rule per country.
 *
 * `digits: null` means the country has no postal-code system at all — the UAE
 * and Qatar use PO boxes — so the field must be genuinely optional there rather
 * than validated to some other length.
 */
export interface PostalCodeRule {
    /** Field label, e.g. "Pincode" in India, "Postal Code" elsewhere. */
    label: string;
    /** Exact digit lengths accepted, or null where no postal code exists. */
    digits: readonly number[] | null;
    /** Placeholder for the input. Empty when the field does not apply. */
    example: string;
    /**
     * An extra shape constraint, applied on top of {@link digits}.
     *
     * Only India needs one: a PIN code never starts with zero, so `041001` is
     * six digits and still not a real address. The length check alone let it
     * through, which meant a location or supplier could be saved with a PIN
     * that cannot exist.
     */
    pattern?: RegExp;
}

export const POSTAL_CODE_RULES: Readonly<Record<string, PostalCodeRule>> = Object.freeze({
    IN: Object.freeze({
        label: 'Pincode',
        digits: Object.freeze([6]),
        example: '560001',
        // Postal circles run 1-8; no PIN begins with 0 or 9.
        pattern: /^[1-9][0-9]{5}$/,
    }),
    SA: Object.freeze({ label: 'Postal Code', digits: Object.freeze([5]), example: '11564' }),
    KW: Object.freeze({ label: 'Postal Code', digits: Object.freeze([5]), example: '13001' }),
    OM: Object.freeze({ label: 'Postal Code', digits: Object.freeze([3]), example: '133' }),
    BH: Object.freeze({ label: 'Postal Code', digits: Object.freeze([3, 4]), example: '317' }),
    // No postal-code system — addresses use PO boxes, so there is no fixed
    // length to validate. The column is VARCHAR(10) and holds the box number.
    AE: Object.freeze({ label: 'PO Box', digits: null, example: '12345' }),
    QA: Object.freeze({ label: 'PO Box', digits: null, example: '12345' }),
});

/**
 * A representative city, used only as an input placeholder so the form does not
 * suggest "Mumbai" to a tenant in Muscat. Presentation only — never stored,
 * never compared.
 */
export const EXAMPLE_CITY: Readonly<Record<string, string>> = Object.freeze({
    IN: 'Mumbai',
    AE: 'Dubai',
    SA: 'Riyadh',
    QA: 'Doha',
    OM: 'Muscat',
    BH: 'Manama',
    KW: 'Kuwait City',
});

/** Placeholder city for a country, blank when we hold no example. */
export function getExampleCity(iso: string): string {
    return EXAMPLE_CITY[iso] ?? '';
}

/** Regions to offer for a country, or an empty list when we hold no data. */
export function getRegionsForCountry(iso: string): readonly string[] {
    return COUNTRY_REGIONS[iso] ?? [];
}

/** What this country calls a region. Falls back to the neutral "Region". */
export function getRegionLabel(iso: string): string {
    return REGION_LABEL[iso] ?? 'Region';
}

/** The postal-code rule for a country, defaulting to a free-form optional field. */
export function getPostalCodeRule(iso: string): PostalCodeRule {
    return POSTAL_CODE_RULES[iso] ?? { label: 'Postal Code', digits: null, example: '' };
}

/**
 * Validate a postal code for a country. Blank always passes — the field is
 * optional everywhere — and a country with no postal system accepts anything,
 * so a UAE tenant is never blocked by a field their addresses do not use.
 */
export function isValidPostalCode(value: string, iso: string): boolean {
    const trimmed = value.trim();
    if (trimmed === '') return true;
    const rule = getPostalCodeRule(iso);
    if (rule.digits === null) return true;
    if (!/^\d+$/.test(trimmed) || !rule.digits.includes(trimmed.length)) return false;
    return rule.pattern ? rule.pattern.test(trimmed) : true;
}
